/**
 * Guide engine v3 - package loader (#1023, epic #1020). Spec R1 / R9 / R11.
 *
 * Fetches a v3 guide package JSON from the shared guide-content CDN, falling
 * back to the default locale when the requested one is missing, and validates it
 * against the contract before returning a typed package. JSON only - the engine
 * never fetches or evals `.js`. A fetch/validate failure throws; the runtime maps
 * the throw to the error chip (R11). No v1 reuse.
 */

import { getCachedConfig } from '../services/configService';
import { DEFAULT_LOCALE } from './contract';
import { validateGuidePackage } from './validator';
import { ensureCdnCookies } from './cdnAuth';

const isNonEmptyString = (v) => typeof v === 'string' && v.length > 0;

/**
 * Resolve the guide-content CDN base URL from runtime config.
 * @returns {string} base URL with any trailing slash stripped.
 */
export const guideCdnBaseUrl = () => {
  const cfg = getCachedConfig();
  const base = cfg && cfg.guideContentCdnUrl;
  if (!isNonEmptyString(base)) throw new Error('guide content CDN URL is not configured');
  return base.replace(/\/+$/, '');
};

/**
 * Fetch one locale file. Returns the parsed JSON, or null if the file is absent
 * (404). Other transport / parse failures throw.
 * @param {string} base       CDN base url (no trailing slash).
 * @param {string} key        package key `<slug>/<version>/` (no leading slash).
 * @param {string} locale     locale file stem, e.g. `en-US`.
 * @returns {Promise<unknown|null>}
 */
const fetchLocale = async (base, key, locale) => {
  const url = `${base}/${key}${locale}.json`;
  const doFetch = async () => {
    try {
      // credentials so the signed CDN cookies ride along (#1566).
      return await fetch(url, { credentials: 'include' });
    } catch (err) {
      throw new Error(`guide package fetch failed: ${err.message}`);
    }
  };
  await ensureCdnCookies();
  let res = await doFetch();
  if (res.status === 403) {
    // Auth-expiry recovery (spec R11 transport carve-out): one forced cookie
    // refresh + one retry; a second 403 falls through to the throw (chip).
    await ensureCdnCookies({ force: true });
    res = await doFetch();
  }
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`guide package fetch failed: ${res.status}`);
  return res.json();
};

/**
 * Fetch + validate one v3 guide package, with locale fallback (R9).
 *
 * Tries `<key><locale>.json`; if that locale is absent (404) and it is not
 * already the default, falls back to `<key>en-US.json`. The returned package is
 * validated (R1 shape + R14 checks 1-9) - an invalid package throws naming the
 * failed check.
 *
 * @param {string} packageKey  S3 prefix `<slug>/<version>/`.
 * @param {string} [locale]    locale file stem; defaults to en-US.
 * @param {{moduleId?:string, version?:string}} [expected]  catalog row to integrity-check against.
 * @returns {Promise<import('./contract').GuidePackage>}
 */
export const loadGuidePackage = async (packageKey, locale = DEFAULT_LOCALE, expected = {}) => {
  if (!isNonEmptyString(packageKey)) throw new Error('loadGuidePackage requires a packageKey');
  const base = guideCdnBaseUrl();
  const key = packageKey.replace(/^\/+/, '');

  let raw = await fetchLocale(base, key, locale);
  if (raw == null && locale !== DEFAULT_LOCALE) {
    raw = await fetchLocale(base, key, DEFAULT_LOCALE);
  }
  if (raw == null) throw new Error(`guide package not found: ${key} (locale ${locale})`);

  const pkg = validateGuidePackage(raw);

  if (expected.moduleId && pkg.moduleId !== expected.moduleId) {
    throw new Error(`guide package moduleId mismatch: ${pkg.moduleId} != ${expected.moduleId}`);
  }
  if (expected.version && pkg.version !== expected.version) {
    throw new Error(`guide package version mismatch: ${pkg.version} != ${expected.version}`);
  }
  return pkg;
};
